import { APP_ID, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastModule } from 'primeng/toast';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HomeModule } from './modules/home/home.module';
import { LoginModule } from './modules/login/login.module';
import { CoreModule } from './core/core.module';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { providePrimeNG } from 'primeng/config';
import { GuaiPreset } from './core/theme/guai-preset';
import { RolesModule } from './modules/roles/roles.module';
import { ProvidersModule } from './modules/providers/providers.module';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { HttpErrorInterceptor } from './core/interceptors/http-error.interceptor';
import { CapabilitiesModule } from './modules/capabilities/capabilities.module';
import { AuthenticationsServicesModule } from './modules/authentications-services/authentications-services.module';
import { UsersModule } from './modules/users/users.module';
import { UsersActivityModule } from './modules/users-activity/users-activity.module';
import { PlatformHealthModule } from './modules/platform-health/platform-health.module';
import { ServiceLogsModule } from './modules/service-logs/service-logs.module';
import { CalendarsModule } from './modules/calendars/calendars.module';
import { BusinessModule } from './modules/business/business.module';
import { CompaniesModule } from './modules/companies/companies.module';
import { JsonDateInterceptor } from './core/interceptors/http-json-date.interceptor';
import { WorkersModule } from './modules/workers/workers.module';
import { ReactiveFormsModule } from '@angular/forms';
import { ReactiveFormConfig, RxReactiveFormsModule } from '@rxweb/reactive-form-validators';
import { BanksModule } from './modules/banks/banks.module';
import { GuardsShiftsModule } from './modules/guards-shifts/guards-shifts.module';
import { ClientsModule } from './modules/clients/clients.module';
import { CasesModule } from './modules/cases/cases.module';
import { EquipmentsModule } from './modules/equipments/equipments.module';
import { MaterialsModule } from './modules/materials/materials.module';
import { VehiclesTypesModule } from './modules/vehicles-types/vehicles-types.module';
import { VehiclesModule } from './modules/vehicles/vehicles.module';
import { GuaranteesModule } from './modules/guarantees/guarantees.module';
import { IMqttServiceOptions, MqttModule } from 'ngx-mqtt';
import { environment } from 'src/environment/environment';
import { ChatModule } from './modules/chat/chat.module';
import { SidebarModule } from 'primeng/sidebar';
import { RatesModule } from './modules/rates/rates.module';
import { NotificationsModule } from './modules/notifications/notifications.module';
import { TasksModule } from './modules/tasks/tasks.module';
import { TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { SurveysModule } from './modules/surveys/surveys.module';
import { TemplatesModule } from './modules/templates/templates.module';
import { TransactionsModule } from './modules/transactions/transactions.module';
import { ExpertsModule } from './modules/experts/experts.module';
import { CasesTypesModule } from './modules/cases-types/cases-types.module';
import { PublicSurveyResponseModule } from './modules/public/surveys/public-survey-response.module';
import { ComunicationsModule } from './modules/comunications/comunications.module';
import { TransactionsSeriesModule } from './modules/transactions-series/transactions-series.module';
import { PhoneModule } from './modules/phone/phone.module';
import { VOIPModule } from './modules/voip/voip.module';
import { PublicCaseInfoModule } from './modules/public/case/public-case-info.module';
import { ZonesModule } from './modules/zones/zones.module';
import { TaxesModule } from './modules/taxes/taxes.module';
import { GroupsModule } from './modules/groups/groups.module';
import { ReportsModule } from './modules/reports/reports.module';
import { SeriousCasesModule } from './modules/serious-cases/serious-cases.module';
import { CookieService } from 'ngx-cookie-service';
import { CasesServicesReasonsModule } from './modules/cases-services-reasons/cases-services-reasons.module';
import { ThirdPartiesSourcesModule } from './modules/third-parties-sources/third-parties-sources.module';
import { CasesStatesModule } from './modules/cases-states/cases-states.module';
import { IncidentsTypesModule } from './modules/incidents-types/incidents-types.module';
import { AutomationsModule } from './modules/automations/automations.module';
import { CompanyContractsModule } from './modules/company-contracts/company-contracts.module';
import { MailsAccountsModule } from './modules/mails-accounts/mails-accounts.module';
import { MailboxModule } from './modules/mailbox/mailbox.module';
import { CostsCentersModule } from './modules/costs-center/costs-centers.module';
import { CasesServicesCausesModule } from './modules/cases-services-causes/cases-services-causes.module';
import { ServicesTypesModule } from './modules/services-types/services-types.module';
import { CasesLogTypesModule } from './modules/cases-log-types/cases-log-types.module';
import { DatasetsModule } from './modules/datasets/datasets.module';
import { ImportsModule } from './modules/imports/imports.module';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { TransactionsRevisionsModule } from './modules/transactions-revision-reasons/transactions-revision-reasons.module';
import { PublicLegalModule } from './modules/public/legal/public-legal.module';
import { InsuranceCompaniesModule } from './modules/insurance-companies/insurance-companies.module';
import { WhatsAppModule } from './modules/whatsapp/whatsapp.module';
import { UseModule } from './modules/use/use.module';
import { DataProtectionModule } from './modules/data-protection/data-protection.module';
import { WorkerEndReasonsModule } from './modules/worker-end-reasons/worker-end-reasons.module';
import { CasesPendingApprovalModule } from './modules/cases-pending-approval/cases-pending-approval.module';
import { DelegationsModule } from './modules/delegations/delegations.module';
import { ColectiveAgreementModule } from './modules/colective-agreement/colective-agreement.module';
import { ProfessionalCategoryModule } from './modules/professional-category/professional-category.module';
import { TradesModule } from './modules/trades/trades.module';
import { MediatorsModule } from './modules/mediators/mediators.module';
import { ConfigurationsModule } from './modules/configurations/configurations.module';
import { HelpCenterModule } from './modules/help-center/help-center.module';
import { SettingsModule } from './modules/settings/settings.module';
import { CasesTreatmentsModule } from './modules/cases-treatments/cases-treatments.module';
import { ProductsModule } from './modules/products/products.module';

//configuración mqtt
const MQTT_SERVICE_OPTIONS: IMqttServiceOptions = {
  hostname: environment.mqtt.server,
  port: environment.mqtt.port,
  protocol: (environment.mqtt.protocol === "wss") ? "wss" : "ws",
  path: '',
  username: environment.mqtt.username,
  password: environment.mqtt.password,
  reconnectPeriod: 10 * 1000,
};

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    MqttModule.forRoot(MQTT_SERVICE_OPTIONS),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ReactiveFormsModule,
    RxReactiveFormsModule,
    CoreModule,
    LoginModule,
    HomeModule,
    AuthenticationsServicesModule,
    ProvidersModule,
    WorkersModule,
    CalendarsModule,
    CompaniesModule,
    RolesModule,
    CapabilitiesModule,
    UsersModule,
    UsersActivityModule,
    PlatformHealthModule,
    ServiceLogsModule,
    TasksModule,
    SurveysModule,
    TemplatesModule,
    ExpertsModule,
    CasesTypesModule,
    CasesServicesReasonsModule,
    CasesServicesCausesModule,
    ThirdPartiesSourcesModule,
    BusinessModule,
    BanksModule,
    ClientsModule,
    CasesModule,
    SeriousCasesModule, 
    CasesStatesModule,
    IncidentsTypesModule,
    GuaranteesModule,
    ToastModule,
    CoreModule,
    GuardsShiftsModule,
    EquipmentsModule,
    MaterialsModule,
    VehiclesTypesModule,
    ServicesTypesModule,
    VehiclesModule,
    ChatModule,
    SidebarModule,
    RatesModule,
    NotificationsModule,
    TransactionsModule,
    TransactionsSeriesModule,
    PublicSurveyResponseModule,
    ComunicationsModule,
    PhoneModule,
    VOIPModule,
    PublicCaseInfoModule,
    PublicLegalModule,
    GroupsModule,
    ZonesModule,
    TaxesModule,
    ReportsModule,
    AutomationsModule,
    CompanyContractsModule,
    MailsAccountsModule,
    MailboxModule,
    CostsCentersModule,
    CasesLogTypesModule,
    DatasetsModule,
    ImportsModule,
    TransactionsRevisionsModule,
    InsuranceCompaniesModule,
    WhatsAppModule,
    MonacoEditorModule.forRoot(),
    UseModule,
    DataProtectionModule,
    WorkerEndReasonsModule,
    CasesPendingApprovalModule,
    DelegationsModule,
    ColectiveAgreementModule,
    ProfessionalCategoryModule,
    TradesModule,
    MediatorsModule,
    ConfigurationsModule,
    HelpCenterModule,
    SettingsModule,
    CasesTreatmentsModule,
    ProductsModule
  ],
  providers: [
    { provide: APP_ID, useValue: 'ng-cli-universal' },
    { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
    { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JsonDateInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    CookieService,
    MessageService,
    DialogService,
    ConfirmationService,
    providePrimeNG({
      theme: {
        preset: GuaiPreset,
        options: {
          darkModeSelector: false,
          cssLayer: { name: 'primeng', order: 'primeng' }
        }
      }
    })
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  public constructor() {
    //registramos locales
    registerLocaleData(localeEs);

    //configuramos mensajes de validación
    ReactiveFormConfig.set({
      validationMessage: {
        required: 'Este campo es requerido',
        minLength: 'Este campo debe tener al menos {{1}} caracteres'
      }
    });
  }
}
